/**
 * MeetingActions — "📝 Notes → Actions".
 *
 * Paste raw meeting notes → `POST /ai/copilot/meeting-actions` returns a
 * summary, the decisions taken, actionable items (title / owner / due / prio),
 * and any flagged risks. Each action item can be turned into a real corrective
 * action via `POST /compliance-pro/actions` (governance-service) — the SAME
 * wired endpoint FrameworkGap + reg-impact write to. (The former default
 * `POST /action-items` had no gateway route and 405'd — bug #D5.)
 * CONFIRM-GATED per item (click → "Confirmer" before the write happens).
 *
 * Renders INLINE (toggle → expandable panel, never a nested Dialog) so it can
 * live inside the GRC Copilot sidebar (itself a Dialog) without breaking Radix
 * focus trapping.
 */

import * as React from 'react'
import {
  ClipboardList,
  Loader2,
  Wand2,
  X,
  ListChecks,
  Gavel,
  AlertTriangle,
  ShieldCheck,
  Check,
  Plus,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import apiClient from '@/lib/api/client'
import { useMeetingActions, type MeetingActionItem } from '@/services/copilot.service'

export interface MeetingActionsProps {
  /** Module hint forwarded to the backend for grounding. */
  module?: string
  /** Endpoint that creates a task/action (defaults to the wired /compliance-pro/actions). */
  createEndpoint?: string
  className?: string
}

const PRIORITY_STYLE: Record<string, string> = {
  critical: 'bg-red-500/10 text-red-600 border-red-500/30',
  high: 'bg-red-500/10 text-red-600 border-red-500/30',
  medium: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
  low: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
}

type ItemState = 'idle' | 'confirming' | 'creating' | 'created'

export function MeetingActions({
  module,
  createEndpoint = '/compliance-pro/actions',
  className,
}: MeetingActionsProps) {
  const [open, setOpen] = React.useState(false)
  const [notes, setNotes] = React.useState('')
  const [itemStates, setItemStates] = React.useState<Record<number, ItemState>>({})
  const meetingM = useMeetingActions()
  const result = meetingM.data

  const run = async () => {
    if (!notes.trim()) return
    setItemStates({})
    try {
      await meetingM.mutateAsync({ notes: notes.trim(), module })
    } catch {
      toast.error("L'analyse des notes a échoué. Réessayez.")
    }
  }

  const setState = (idx: number, s: ItemState) =>
    setItemStates((prev) => ({ ...prev, [idx]: s }))

  const createTask = async (idx: number, item: MeetingActionItem) => {
    setState(idx, 'creating')
    // Map the AI action-item fields onto the /compliance-pro/actions body.
    // `owner` → `assignee` (the persisted column); `due_hint` is freeform text
    // (e.g. "fin du mois") so it is passed through / folded into the
    // description — NOT into `due_date`, which is DATE-cast and would soft-fail.
    const payload: Record<string, unknown> = {
      title: item.title,
      priority: (item.priority ?? 'medium').toLowerCase(),
      status: 'todo',
      ...(item.owner ? { assignee: item.owner } : {}),
      ...(item.due_hint ? { due_hint: item.due_hint } : {}),
      description: [item.owner ? `Responsable: ${item.owner}` : '', item.due_hint ? `Échéance: ${item.due_hint}` : '']
        .filter(Boolean)
        .join(' · '),
      source: 'meeting-actions',
    }
    try {
      await apiClient.post(createEndpoint, payload)
      setState(idx, 'created')
      toast.success('Tâche créée')
    } catch {
      setState(idx, 'idle')
      toast.error('Création de la tâche impossible pour le moment.')
    }
  }

  if (!open) {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className={cn('h-7 gap-1.5 rounded-full border-primary/40 text-xs text-primary', className)}
        onClick={() => setOpen(true)}
      >
        <ClipboardList className="h-3.5 w-3.5" />
        Notes → Actions
      </Button>
    )
  }

  return (
    <div className={cn('w-full space-y-2 rounded-lg border border-primary/30 bg-primary/5 p-2.5', className)}>
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-1.5 text-xs font-semibold text-primary">
          <ClipboardList className="h-3.5 w-3.5" />
          Notes de réunion → Actions
        </p>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-5 w-5"
          onClick={() => setOpen(false)}
          aria-label="Fermer"
        >
          <X className="h-3 w-3" />
        </Button>
      </div>

      <Textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Collez les notes de la réunion (décisions, points d'action, risques évoqués)…"
        className="min-h-[70px] bg-background text-xs"
      />
      <div className="flex justify-end">
        <Button size="sm" className="h-7 text-xs" onClick={run} disabled={meetingM.isPending || !notes.trim()}>
          {meetingM.isPending ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Wand2 className="mr-1.5 h-3.5 w-3.5" />
          )}
          Analyser
        </Button>
      </div>

      {meetingM.isError ? (
        <p className="text-xs text-destructive">L'analyse a échoué. Réessayez dans un instant.</p>
      ) : null}

      {result ? (
        <ScrollArea className="max-h-[46vh]">
          <div className="space-y-3 border-t pt-2 pr-1">
            {result.summary ? (
              <p className="whitespace-pre-wrap text-xs leading-relaxed">{result.summary}</p>
            ) : null}

            {result.decisions.length > 0 ? (
              <div>
                <p className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <Gavel className="h-3 w-3" /> Décisions ({result.decisions.length})
                </p>
                <ul className="space-y-1">
                  {result.decisions.map((d, i) => (
                    <li key={i} className="rounded-md bg-muted/40 px-2 py-1 text-xs">
                      {d.text}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {result.action_items.length > 0 ? (
              <div>
                <p className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <ListChecks className="h-3 w-3" /> Actions ({result.action_items.length})
                </p>
                <ul className="space-y-1.5">
                  {result.action_items.map((a, i) => {
                    const st = itemStates[i] ?? 'idle'
                    return (
                      <li key={i} className="rounded-md border bg-background px-2 py-1.5">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-xs font-medium">{a.title}</p>
                            <div className="mt-0.5 flex flex-wrap items-center gap-1">
                              {a.priority ? (
                                <Badge
                                  variant="outline"
                                  className={cn('text-[9px]', PRIORITY_STYLE[(a.priority ?? '').toLowerCase()] ?? PRIORITY_STYLE.medium)}
                                >
                                  {a.priority}
                                </Badge>
                              ) : null}
                              {a.owner ? (
                                <Badge variant="secondary" className="text-[9px]">{a.owner}</Badge>
                              ) : null}
                              {a.due_hint ? (
                                <span className="text-[10px] text-muted-foreground">{a.due_hint}</span>
                              ) : null}
                            </div>
                          </div>
                          <div className="shrink-0">
                            {st === 'created' ? (
                              <Badge variant="secondary" className="gap-1 text-[9px] text-green-600">
                                <Check className="h-3 w-3" /> Créée
                              </Badge>
                            ) : st === 'confirming' ? (
                              <div className="flex items-center gap-1">
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-6 w-6"
                                  onClick={() => setState(i, 'idle')}
                                  aria-label="Annuler"
                                >
                                  <X className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                  size="sm"
                                  className="h-6 gap-1 px-2 text-[10px]"
                                  onClick={() => createTask(i, a)}
                                >
                                  <ShieldCheck className="h-3 w-3" /> Confirmer
                                </Button>
                              </div>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-6 gap-1 px-2 text-[10px]"
                                disabled={st === 'creating'}
                                onClick={() => setState(i, 'confirming')}
                              >
                                {st === 'creating' ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <Plus className="h-3 w-3" />
                                )}
                                Créer une tâche
                              </Button>
                            )}
                          </div>
                        </div>
                      </li>
                    )
                  })}
                </ul>
              </div>
            ) : null}

            {result.risks_flagged.length > 0 ? (
              <div>
                <p className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <AlertTriangle className="h-3 w-3" /> Risques signalés ({result.risks_flagged.length})
                </p>
                <ul className="space-y-1">
                  {result.risks_flagged.map((r, i) => (
                    <li key={i} className="rounded-md bg-red-500/5 px-2 py-1 text-xs text-red-700 dark:text-red-400">
                      {r}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </ScrollArea>
      ) : null}
    </div>
  )
}

export default MeetingActions
